import { useQuery } from '@tanstack/react-query';
import {
  PkAccordionItem,
  PkAccordionItemContent,
  PkAccordionItemTitle,
  PkDropdown,
  PkIcon,
  PkMenu,
  PkMenuItem,
} from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import { getTeamFormGuideQuery } from '@queries/cards';
import { getMatchUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import FormGuideAvatar from '@shared/FormGuideAvatar';
import { useTranslateFn } from '@shared/Translate';
import type { PkTheme } from '@shared-types/common.types';
import type { FormGuideMatch } from '@shared-types/form-guide.types';
import { dateToString } from '@utilities/dateTime';
import { matchScore } from '@utilities/live';

import * as styles from './styles.module.scss';

export type TeamFormGuideOptions = {
  teamId: string;
  theme: PkTheme;
};

const numberOfMatches = 5;

const FgMatch = memo(
  ({ formGuideMatch, teamId, menuItem }: { formGuideMatch: FormGuideMatch; teamId: string; menuItem?: boolean }) => {
    const { match, outcome } = formGuideMatch;
    const homeScore = matchScore(match, 'home') ?? 0;
    const awayScore = matchScore(match, 'away') ?? 0;

    const label = `${hydrateWithTranslations(match.homeTeam)?.displayName ?? ''} ${homeScore} - ${awayScore} ${hydrateWithTranslations(match.awayTeam)?.displayName ?? ''}`;
    const subLabel = useMemo(
      () =>
        [
          dateToString(match.kickOffTime?.dateTime ?? match.kickOffTime?.date, 'D MMM YYYY'),
          hydrateWithTranslations(match.competition)?.name,
        ].join(' • '),
      [match.kickOffTime, match.competition],
    );

    const isDomestic = match.competition?.region === 'DOMESTIC';
    const Tag = useMemo(() => {
      if (menuItem) return PkMenuItem;

      return isDomestic ? 'div' : 'a';
    }, [isDomestic, menuItem]);
    const link = useMemo(() => (isDomestic ? null : getMatchUrl(match.id)), [isDomestic, match.id]);

    return (
      <Tag className={clsx(styles.match, isDomestic && styles.matchDomestic)} href={link}>
        <FormGuideAvatar outcome={outcome} match={match} teamId={teamId} size={32} textClassName={styles.textBig} />
        <div className='pk-d--flex pk-flex--column pk-justify-content--center pk-text--text-02 pk-font-size--s'>
          <div>{label}</div>
          <div className='pk-text--text-03 pk-font-size--xs2'>{subLabel}</div>
        </div>
      </Tag>
    );
  },
);
FgMatch.displayName = 'FgMatch';

const TeamFormGuide = ({ options }: { options: TeamFormGuideOptions }) => {
  const { isDesktop } = useWindowSize();
  const t = useTranslateFn();

  const { data: matches } = useQuery({
    ...getTeamFormGuideQuery(options.teamId, numberOfMatches),
    select: (response) => response?.data?.find((fg) => fg.team.id === options.teamId)?.formguide ?? [],
  });

  const matchesPlaceholders = useMemo(
    () => Array.from({ length: matches ? numberOfMatches - matches.length : numberOfMatches }, (_, i) => i),
    [matches],
  );

  const shortList = useMemo(
    () => (
      <div className='pk-d--flex pk-gap--xs2'>
        {matchesPlaceholders.map((i) => (
          <div key={`placeholder-${i}`} className={styles.placeholder} />
        ))}
        {matches?.toReversed().map(({ match, outcome }, i) => (
          <FormGuideAvatar
            key={match.id}
            outcome={outcome}
            match={match}
            teamId={options.teamId}
            size={18}
            underline={i === matches.length - 1}
          />
        ))}
      </div>
    ),
    [matches, matchesPlaceholders, options.teamId],
  );

  if (!matches?.length) return null;

  return (
    <div className={styles.formGuide}>
      {isDesktop ? (
        <>
          <div className='pk-text--text-01 pk-font-size--s pk-line-height--s pk-mb--xs'>{t('form_guide')}</div>
          <PkDropdown className={clsx(styles.matchList, 'pk-theme--default')} placement='bottom-end'>
            <div className='pk-d--flex pk-gap--xs' slot='trigger'>
              {shortList}
              <PkIcon pk-theme={options.theme} name='navigation-chevron-down' size='20' color='ui-04' />
            </div>
            <PkMenu hideSectionDivider hideItemsDivider>
              <div slot='header'>
                <div className='pk-font-size--l pk-font--base-semibold pk-text--text-01 pk-line-height--s'>
                  {t('form_guide')}
                </div>
                <div className='pk-font-size--s pk-text--text-02 pk-line-height--s pk-mt--xs'>
                  {t('last_n_matches', { replace: `${Math.min(numberOfMatches, matches.length)}` })}
                </div>
              </div>
              {matches.map((m) => (
                <FgMatch key={m.match.id} formGuideMatch={m} teamId={options.teamId} menuItem />
              ))}
            </PkMenu>
          </PkDropdown>
        </>
      ) : (
        <PkAccordionItem hasDivider={false} expanded={false} className='pk-pb--0 pk-mb--0'>
          <PkAccordionItemTitle
            className={styles.accordionTitle}
            iconPosition='right'
            circleIcon={false}
            iconColor='ui-06'
          >
            <div className='pk-text--text-01 pk-font-size--s pk-line-height--s pk-mb--xs'>{t('form_guide')}</div>
            {shortList}
          </PkAccordionItemTitle>
          <PkAccordionItemContent className={styles.accordionContent}>
            {matches.map((m) => (
              <FgMatch key={m.match.id} formGuideMatch={m} teamId={options.teamId} />
            ))}
          </PkAccordionItemContent>
        </PkAccordionItem>
      )}
    </div>
  );
};

export default memo(TeamFormGuide);
